import { BigSlotLayout } from 'Layouts/Farm/SlotLayouts/BigSlotLayout';
import { CropSlotLayout } from 'Layouts/Farm/SlotLayouts/CropSlotLayout';
import { HouseSlotLayout } from 'Layouts/Farm/SlotLayouts/HouseSlotLayout';
import { FenceSlotLayout } from 'Layouts/Farm/SlotLayouts/FenceSlotLayout';
import { SmallSlotLayout } from 'Layouts/Farm/SlotLayouts/SmallSlotLayout';
import Point from 'Utility/Point';
import { FarmSlotId } from 'State/Player/Farm/IFarmPlayerState';
import { report } from 'report';
import { isWebPSupported } from 'Utility/WebPFunctions';
import TransformComponent from 'Component/Transform';
import { FarmSlotType, isFarmSlotType } from './Data/FarmSlotType';
import { FarmItemUnlockType } from './Data/FarmItemUnlockType';
import { ProductId } from 'Utility/Product';
import { sendEvent, track } from 'Utility/Tracking/Track';
import { FarmTrackingEventName } from 'Utility/Tracking/TrackingEvent';
import { IGameState } from 'State/Game/IGameState';
import { isIOS } from 'Utility/Platform';
import {
    addDragonbonesResources,
    areDBResourcesLoaded,
    awaitDragonbonesResources,
} from 'Utility/Resource/ResourceHelper';
import Game from 'Game';
import { DRAGONBONES_PATH } from 'Constants/Dragonbones';
import { CACHED_FARM_ITEM_DATA, FarmItemId, getFarmItemByID } from 'System/Farm/Data/FarmItemData';

/**
 * Gets the armature name based on slot type and item id to animate the item selection
 * @param itemId The id of them item
 * @returns The armature name to be used in DB to animate the item/slot
 */
export function getSlotArmatureName(itemId: FarmItemId): string {
    return 'place_' + getFarmItemByID(itemId).farmItemType;
}

export function parseFarmSlotId(
    id: FarmSlotId
): {
    type: FarmSlotType;
    index: number;
} {
    const idParts = id?.split('_') ?? [];
    const type = idParts[0] as FarmSlotType;
    const index = Number(idParts[1]);
    return {
        type: isFarmSlotType(type) ? type : null,
        index,
    };
}

export function getFarmItemsBySlotType(slotType: FarmSlotType): FarmItemId[] {
    return CACHED_FARM_ITEM_DATA()
        .filter(farmItem => farmItem.slotType === slotType)
        .map(farmItem => farmItem.id);
}

function getItemTexturePathById(itemId: FarmItemId) {
    const item = getFarmItemByID(itemId);

    let subfolder: string;

    switch (item.farmItemUnlockType) {
        case FarmItemUnlockType.CROP:
            subfolder = '/crop/';
            break;
        case FarmItemUnlockType.WORLD:
            subfolder = `/world/${item.world}/`;
            break;
        case FarmItemUnlockType.PREMIUM:
            subfolder = '/premium/';
            break;
    }

    return `resource/graphics/farm_items${subfolder}${itemId}${isWebPSupported() ? '.png.webp' : '.png'}`;
}

export function getFullFarmItemAssetDetails(_itemID: FarmItemId) {
    const dragonbonesAsset = getFarmItemByID(_itemID).dragonbonesAsset;

    return {
        isLoaded: () => {
            if (dragonbonesAsset !== undefined) {
                return areDBResourcesLoaded(dragonbonesAsset);
            }
            return Game.resource.existsAndLoaded(_itemID);
        },
        preload: async () => {
            if (dragonbonesAsset !== undefined) {
                addDragonbonesResources(dragonbonesAsset, `${DRAGONBONES_PATH}/Farm/Decorations/`);
                return awaitDragonbonesResources(dragonbonesAsset);
            }

            const path = getItemTexturePathById(_itemID);
            Game.resource.add(_itemID, path);

            return Game.resource.wait(_itemID);
        },
    };
}

export function getAssetName(itemId: FarmItemId): string {
    return itemId;
}

export function getFarmItemThumbnailAssetName(itemId: FarmItemId): string {
    return `${getAssetName(itemId)}-thumb`;
}

// TODO: check for webp?
export function getFarmItemThumbnailPath(itemId: FarmItemId): string {
    return `resource/graphics/farm_items/thumbnails/${getFarmItemThumbnailAssetName(itemId)}.png`;
}

export function handleItemSize(object: TransformComponent, limits: { width: number; height: number }): void {
    while (object.width > limits.width || object.height > limits.height) {
        object.scale.x -= 0.05;
        object.scale.y -= 0.05;
    }
}

export function sortFarmItemsBySlotType(items: FarmItemId[]): FarmItemId[] {
    items.sort((a, b): number => {
        const aSlotType = getFarmItemByID(a).slotType;
        const bSlotType = getFarmItemByID(b).slotType;
        if (aSlotType === FarmSlotType.SMALL) return -1;
        if (aSlotType === FarmSlotType.BIG && (bSlotType === FarmSlotType.HOUSE || bSlotType === FarmSlotType.FENCE))
            return 0;
        if (aSlotType === FarmSlotType.FENCE && (bSlotType === FarmSlotType.SMALL || bSlotType === FarmSlotType.BIG))
            return 1;
        if (aSlotType === FarmSlotType.HOUSE) return 1;
    });
    return items;
}

export function trackNewItemPlaced(
    state: IGameState,
    slotId: FarmSlotId,
    prevItemId: FarmItemId,
    newItemId: FarmItemId
) {
    try {
        // Only send tracking event if item has changed.
        if (prevItemId !== newItemId) {
            // Do not track empty slots as being "spent".
            if (prevItemId) {
                track('productsSpent', {
                    items: [getItemData(prevItemId)],
                });
            }

            // Do not track empty slots as "received".
            if (newItemId) {
                track('productsReceived', {
                    items: [getItemData(newItemId)],
                });
            }

            sendEvent(FarmTrackingEventName.myFarmUpdate, state);
        }
    } catch (error) {
        report('Failed to track new item selection', {
            once: true,
            sampleRate: 0.1,
            error,
            tags: {
                prevItemId,
                newItemId,
            },
            fingerprint: 'FailedToTrack:trackNewItemPlaced',
        });
    }
    return;
    function getItemData(itemId: FarmItemId) {
        return {
            item: {
                itemName: itemId,
                itemType: slotId,
                itemAmount: 1,
            },
        };
    }
}

/**
 * Checks if the area can be purchasable or not based on device and areaId
 * @param areaId The id of the area
 * @returns If the area can be purchasable or not
 */
export function canPurchaseArea(areaId: string) {
    if (isIOS()) {
        return AreaData[areaId].productId === undefined;
    }

    return true;
}

export const FarmSlotTypeLayoutMap: Record<FarmSlotType, FarmSlotLayout> = {
    [FarmSlotType.BIG]: BigSlotLayout,
    [FarmSlotType.CROP]: CropSlotLayout,
    [FarmSlotType.HOUSE]: HouseSlotLayout,
    [FarmSlotType.FENCE]: FenceSlotLayout,
    [FarmSlotType.SMALL]: SmallSlotLayout,
};

export function getFarmItemsByWorld(world: number) {
    return CACHED_FARM_ITEM_DATA().filter(
        item => item.farmItemUnlockType === FarmItemUnlockType.WORLD && item.world === world
    );
}

export function getFarmItemsByField(field: string) {
    return CACHED_FARM_ITEM_DATA().filter(
        item => item.farmItemUnlockType === FarmItemUnlockType.CROP && item.fieldId === field
    );
}

export type FarmSlotLayout =
    | typeof BigSlotLayout
    | typeof CropSlotLayout
    | typeof HouseSlotLayout
    | typeof FenceSlotLayout
    | typeof SmallSlotLayout;

export type IAreaData = {
    offset: Point;
} & (
    | {
          price: number;
          productId?: never;
      }
    | {
          productId: ProductId;
          price?: never;
      }
);

export const AreaData: Record<string, IAreaData> = {
    ['area_0']: { price: 0, offset: new Point(0.5, 0.5) },
    ['area_1']: { price: 15000, offset: new Point(0.75, 0.5) },
    ['area_2']: { price: 25000, offset: new Point(0.3, 0.5) },
    ['area_3']: { price: 20000, offset: new Point(0.75, 0.5) },
    ['area_4']: { price: 30000, offset: new Point(0.55, 0.5)},
};
